import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  StartWebAuthnRegistrationCommand,
  CompleteWebAuthnRegistrationCommand,
  type AuthenticationResultType,
  type CompleteWebAuthnRegistrationCommandInput,
} from "@aws-sdk/client-cognito-identity-provider";
import { getConfig } from "../config";

/**
 * Passwordless SMS-OTP auth using Cognito's CUSTOM_AUTH flow.
 *
 * Flow:
 *   1. startSignIn(phone)  → InitiateAuth(CUSTOM_AUTH). Cognito invokes our
 *      DefineAuthChallenge trigger, which returns CUSTOM_CHALLENGE, then
 *      CreateAuthChallenge runs and SMSes a 6-digit code. The response gives
 *      us a Session string we hold onto.
 *   2. completeSignIn(code) → RespondToAuthChallenge with the code. The
 *      VerifyAuthChallengeResponse trigger compares it; DefineAuthChallenge
 *      then issues tokens.
 *
 * Tokens are persisted to localStorage so the user stays logged in across
 * tabs and reloads. Refresh-token rotation is left as a TODO — for a 30-day
 * refresh token + 1-hour ID token, the user re-OTPs at most monthly.
 */

interface PendingAuth {
  phone: string;
  session: string;
}

let pending: PendingAuth | null = null;

const STORAGE_KEY = "hash.auth.v1";

interface StoredTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
}

async function getClient() {
  const cfg = await getConfig();
  return {
    client: new CognitoIdentityProviderClient({ region: cfg.region }),
    clientId: cfg.userPoolClientId,
  };
}

export async function startSignIn(phone: string): Promise<void> {
  const { client, clientId } = await getClient();

  const res = await client.send(
    new InitiateAuthCommand({
      AuthFlow: "CUSTOM_AUTH",
      ClientId: clientId,
      AuthParameters: { USERNAME: phone },
    })
  );

  if (!res.Session) {
    throw new Error("No session returned; user may not exist");
  }

  pending = { phone, session: res.Session };
}

export async function completeSignIn(code: string): Promise<void> {
  if (!pending) throw new Error("Call startSignIn first");
  const { client, clientId } = await getClient();

  const res = await client.send(
    new RespondToAuthChallengeCommand({
      ChallengeName: "CUSTOM_CHALLENGE",
      ClientId: clientId,
      Session: pending.session,
      ChallengeResponses: {
        USERNAME: pending.phone,
        ANSWER: code.trim(),
      },
    })
  );

  // If the code was wrong but retries remain, Cognito returns a fresh Session
  // and no AuthenticationResult. Save the new session and let the caller retry.
  if (!res.AuthenticationResult) {
    if (res.Session) {
      pending = { ...pending, session: res.Session };
      throw new Error("Incorrect code; try again");
    }
    throw new Error("Authentication failed");
  }

  storeTokens(res.AuthenticationResult);
  pending = null;
}

function storeTokens(result: AuthenticationResultType) {
  const idToken = result.IdToken!;
  // ID tokens are JWTs — read exp from the payload.
  const payload = JSON.parse(atob(idToken.split(".")[1]));
  const stored: StoredTokens = {
    idToken,
    accessToken: result.AccessToken!,
    refreshToken: result.RefreshToken!,
    expiresAt: payload.exp * 1000,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

function loadTokens(): StoredTokens | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredTokens;
  } catch {
    return null;
  }
}

export async function getIdToken(): Promise<string | null> {
  const tokens = loadTokens();
  if (!tokens) return null;

  // If close to expiry (within 60s), refresh.
  if (tokens.expiresAt - Date.now() < 60_000) {
    const refreshed = await refresh(tokens.refreshToken);
    if (!refreshed) {
      signOut();
      return null;
    }
    return refreshed.idToken;
  }

  return tokens.idToken;
}

async function refresh(refreshToken: string): Promise<StoredTokens | null> {
  const { client, clientId } = await getClient();
  try {
    const res = await client.send(
      new InitiateAuthCommand({
        AuthFlow: "REFRESH_TOKEN_AUTH",
        ClientId: clientId,
        AuthParameters: { REFRESH_TOKEN: refreshToken },
      })
    );
    if (!res.AuthenticationResult?.IdToken) return null;
    // Refresh response doesn't include a new refresh token; reuse the existing one.
    const idToken = res.AuthenticationResult.IdToken;
    const payload = JSON.parse(atob(idToken.split(".")[1]));
    const stored: StoredTokens = {
      idToken,
      accessToken: res.AuthenticationResult.AccessToken!,
      refreshToken,
      expiresAt: payload.exp * 1000,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    return stored;
  } catch {
    return null;
  }
}

/**
 * The current access token (used to authorize the WebAuthn enrollment ceremony,
 * issue #43). Unlike getIdToken this does not auto-refresh — enrollment only
 * happens moments after a fresh login, so the token is always current.
 */
export function getAccessToken(): string | null {
  return loadTokens()?.accessToken ?? null;
}

export function signOut(): void {
  localStorage.removeItem(STORAGE_KEY);
  pending = null;
}

export function isSignedIn(): boolean {
  return loadTokens() !== null;
}

// ---------------------------------------------------------------------------
// Passkeys (WebAuthn) — issue #43
//
// We use Cognito's native WebAuthn support, so Cognito owns attestation/
// assertion verification and credential storage. The browser ceremonies run
// here against the same unauthenticated client; sign-in goes through the
// USER_AUTH choice flow with the WEB_AUTHN challenge, enrollment through
// Start/CompleteWebAuthnRegistration (authorized by the access token).
//
// Cognito speaks the WebAuthn JSON serialization (PublicKeyCredentialCreation/
// RequestOptionsJSON and Registration/AuthenticationResponseJSON). The browser
// credentials API wants/produces ArrayBuffers, so we convert at the boundary.
// ---------------------------------------------------------------------------

/** True if this browser/device can do WebAuthn at all. */
export function passkeySupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.credentials
  );
}

// localStorage keys for the per-device enrollment prompt (the issue's resolved
// open-question: per-device state == "this device has no passkey").
const PASSKEY_ENROLLED_KEY = "hash.passkey.enrolled.v1";
const PASSKEY_DONTASK_KEY = "hash.passkey.dontask.v1";

/** Offer enrollment only if the device supports it and the user hasn't already
 * enrolled here or asked us to stop. */
export function shouldOfferEnrollment(): boolean {
  if (!passkeySupported()) return false;
  try {
    return (
      localStorage.getItem(PASSKEY_ENROLLED_KEY) !== "1" &&
      localStorage.getItem(PASSKEY_DONTASK_KEY) !== "1"
    );
  } catch {
    return false;
  }
}

export function markEnrolled(): void {
  try {
    localStorage.setItem(PASSKEY_ENROLLED_KEY, "1");
  } catch {
    /* private mode — ignore */
  }
}

export function markDontAsk(): void {
  try {
    localStorage.setItem(PASSKEY_DONTASK_KEY, "1");
  } catch {
    /* private mode — ignore */
  }
}

function b64urlToBuffer(value: string): ArrayBuffer {
  const pad = value.length % 4 === 0 ? "" : "=".repeat(4 - (value.length % 4));
  const b64 = (value + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

function bufferToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** PublicKeyCredentialCreationOptionsJSON -> the BufferSource form create() wants. */
function toCreationOptions(
  json: Record<string, unknown>
): PublicKeyCredentialCreationOptions {
  const o = { ...json } as Record<string, unknown>;
  o.challenge = b64urlToBuffer(json.challenge as string);
  const user = { ...(json.user as Record<string, unknown>) };
  user.id = b64urlToBuffer(user.id as string);
  o.user = user;
  if (Array.isArray(json.excludeCredentials)) {
    o.excludeCredentials = (json.excludeCredentials as Record<string, unknown>[]).map(
      (c) => ({ ...c, id: b64urlToBuffer(c.id as string) })
    );
  }
  return o as unknown as PublicKeyCredentialCreationOptions;
}

/** PublicKeyCredentialRequestOptionsJSON -> the BufferSource form get() wants. */
function toRequestOptions(
  json: Record<string, unknown>
): PublicKeyCredentialRequestOptions {
  const o = { ...json } as Record<string, unknown>;
  o.challenge = b64urlToBuffer(json.challenge as string);
  if (Array.isArray(json.allowCredentials)) {
    o.allowCredentials = (json.allowCredentials as Record<string, unknown>[]).map(
      (c) => ({ ...c, id: b64urlToBuffer(c.id as string) })
    );
  }
  return o as unknown as PublicKeyCredentialRequestOptions;
}

/** PublicKeyCredential (registration) -> RegistrationResponseJSON. */
function registrationToJSON(cred: PublicKeyCredential): Record<string, unknown> {
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToB64url(r.clientDataJSON),
      attestationObject: bufferToB64url(r.attestationObject),
      transports:
        typeof r.getTransports === "function" ? r.getTransports() : undefined,
    },
  };
}

/** PublicKeyCredential (assertion) -> AuthenticationResponseJSON. */
function assertionToJSON(cred: PublicKeyCredential): Record<string, unknown> {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToB64url(r.clientDataJSON),
      authenticatorData: bufferToB64url(r.authenticatorData),
      signature: bufferToB64url(r.signature),
      userHandle: r.userHandle ? bufferToB64url(r.userHandle) : undefined,
    },
  };
}

/**
 * The deferred check: does this phone number have a passkey on file? Runs only
 * on phone submit (never as-you-type). Returns false on any error/non-200 (the
 * server returns a uniform false, and the route is throttled), so the caller
 * always falls through cleanly to SMS-OTP.
 */
export async function checkPasskeyAvailable(phone: string): Promise<boolean> {
  try {
    const { apiUrl } = await getConfig();
    const res = await fetch(`${apiUrl}/auth/passkey-availability`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ phone }),
    });
    if (!res.ok) return false;
    const body = (await res.json()) as { hasPasskey?: boolean };
    return body.hasPasskey === true;
  } catch {
    return false;
  }
}

/**
 * Sign in with a passkey via the USER_AUTH WEB_AUTHN challenge — no SMS sent.
 * On success the tokens are stored exactly as the SMS path stores them.
 */
export async function signInWithPasskey(phone: string): Promise<void> {
  const { client, clientId } = await getClient();

  const init = await client.send(
    new InitiateAuthCommand({
      AuthFlow: "USER_AUTH",
      ClientId: clientId,
      AuthParameters: { USERNAME: phone, PREFERRED_CHALLENGE: "WEB_AUTHN" },
    })
  );

  if (init.ChallengeName !== "WEB_AUTHN" || !init.Session) {
    throw new Error("Passkey sign-in is unavailable for this account");
  }

  const optionsJson = init.ChallengeParameters?.CREDENTIAL_REQUEST_OPTIONS;
  if (!optionsJson) throw new Error("No passkey challenge returned");

  const assertion = (await navigator.credentials.get({
    publicKey: toRequestOptions(JSON.parse(optionsJson)),
  })) as PublicKeyCredential | null;
  if (!assertion) throw new Error("Passkey sign-in was cancelled");

  const res = await client.send(
    new RespondToAuthChallengeCommand({
      ChallengeName: "WEB_AUTHN",
      ClientId: clientId,
      Session: init.Session,
      ChallengeResponses: {
        USERNAME: phone,
        CREDENTIAL: JSON.stringify(assertionToJSON(assertion)),
      },
    })
  );

  if (!res.AuthenticationResult) throw new Error("Passkey sign-in failed");
  storeTokens(res.AuthenticationResult);
  pending = null;
}

/**
 * Enroll a passkey for the just-signed-in user. Runs the Cognito WebAuthn
 * registration ceremony, then mirrors the credential id into our access ledger
 * so the deferred check can find it. Marks this device enrolled on success.
 */
export async function enrollPasskey(): Promise<void> {
  const { client } = await getClient();
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error("Not signed in");

  const start = await client.send(
    new StartWebAuthnRegistrationCommand({ AccessToken: accessToken })
  );
  const creationJson = start.CredentialCreationOptions as
    | Record<string, unknown>
    | undefined;
  if (!creationJson) throw new Error("Could not start passkey registration");

  const credential = (await navigator.credentials.create({
    publicKey: toCreationOptions(creationJson),
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error("Passkey creation was cancelled");

  await client.send(
    new CompleteWebAuthnRegistrationCommand({
      AccessToken: accessToken,
      // The SDK types Credential as a recursive DocumentType; our plain JSON
      // object satisfies it structurally once cast.
      Credential: registrationToJSON(
        credential
      ) as CompleteWebAuthnRegistrationCommandInput["Credential"],
    })
  );

  // Mirror into our table so the pre-login availability check can find it.
  const { apiUrl } = await getConfig();
  await fetch(`${apiUrl}/me/passkeys`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${(await getIdToken()) ?? ""}`,
    },
    body: JSON.stringify({ credentialId: credential.id }),
  });

  markEnrolled();
}

/** Decoded claims from the current ID token, or null if not signed in. */
export function getClaims(): {
  sub: string;
  hasherId: string;
  role: "admin" | "misman" | "hare";
  // Kennel ids the caller is misman of (kennel-scoped role). Fresh grants
  // appear after the next token refresh or re-login.
  mismanKennels: string[];
  phone: string;
} | null {
  const tokens = loadTokens();
  if (!tokens) return null;
  try {
    const payload = JSON.parse(atob(tokens.idToken.split(".")[1]));
    return {
      sub: payload.sub,
      hasherId: payload["custom:hasherId"],
      role: payload["custom:role"],
      mismanKennels: ((payload["custom:mismanKennels"] as string) ?? "")
        .split(",")
        .map((k: string) => k.trim())
        .filter(Boolean),
      phone: payload.phone_number,
    };
  } catch {
    return null;
  }
}
